import signatureTypes from 'constants/signatureTypes';
import { getEnvelopesPendingBySigners } from 'modules/_shared/redux/envelopeActions';
import { useCallback, useEffect, useState } from 'react';
import { Beneficiary } from 'types';
import { EnvelopeWithStatus } from 'types/Envelope';

type BeneficiaryPending = Beneficiary & {
  beneficiaryNumber: number;
  planName: string;
};

export type BeneficiaryWithEnvelopeId = BeneficiaryPending & {
  envelopeId: string;
  envelopeSignatureId: string;
  envelopeStatus: string;
};

type UseGetBeneficiariesPendingEnvelopesProps = {
  beneficiaries: BeneficiaryPending[];
  currentPage?: number;
  itemsPerPage?: number;
  filterFn?: (beneficiary: BeneficiaryPending) => boolean; // eslint-disable-line no-unused-vars
};

type BeneficiariesPendingEnvelopes = {
  data: BeneficiaryWithEnvelopeId[];
  isLoading: boolean;
  isError: boolean;
  updateBeneficiaryStatus: (
    _beneficiaryId: string, // eslint-disable-line no-unused-vars
    _invitationStatus: string, // eslint-disable-line no-unused-vars
    _envelopeStatus?: string // eslint-disable-line no-unused-vars
  ) => void;
};

const parseResponseToBeneficiariesWithEnvelopeId = (
  response: EnvelopeWithStatus[],
  beneficiaries: BeneficiaryPending[]
): BeneficiaryWithEnvelopeId[] =>
  beneficiaries.map((beneficiary) => {
    const envelope = response.find((envelope) =>
      envelope.signers.some((signer) => signer.id === beneficiary._id)
    );

    return {
      ...beneficiary,
      envelopeId: envelope?._id || '',
      envelopeSignatureId: envelope?.envelopeId || '',
      envelopeStatus: envelope?.envelopeStatus?.envelopeStatus || '',
    };
  });

const useGetBeneficiariesPendingEnvelopes = ({
  beneficiaries,
  currentPage = 1,
  itemsPerPage = 10,
  filterFn,
}: UseGetBeneficiariesPendingEnvelopesProps): BeneficiariesPendingEnvelopes => {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [beneficiariesPendingEnvelopes, setBeneficiariesPendingEnvelopes] =
    useState<BeneficiaryWithEnvelopeId[]>([]);

  const updateBeneficiaryStatus = useCallback(
    (
      beneficiaryId: string,
      invitationStatus: string,
      envelopeStatus?: string
    ) => {
      setBeneficiariesPendingEnvelopes((prev) =>
        prev.map((beneficiary) =>
          beneficiary._id === beneficiaryId
            ? {
                ...beneficiary,
                invitationStatus,
                ...(envelopeStatus && { envelopeStatus }),
              }
            : beneficiary
        )
      );
    },
    []
  );

  const fetchBeneficiariesPendingEnvelopes = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);

    try {
      // Apply filter first if provided
      const filteredBeneficiaries = filterFn
        ? beneficiaries.filter(filterFn)
        : beneficiaries;

      // Calculate pagination for current page
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedBeneficiaries = filteredBeneficiaries.slice(
        startIndex,
        endIndex
      );

      // If no beneficiaries to process, return early
      if (paginatedBeneficiaries.length === 0) {
        setBeneficiariesPendingEnvelopes([]);
        setIsLoading(false);
        return;
      }

      const signerIds = paginatedBeneficiaries
        .map((beneficiary) => beneficiary._id)
        .filter(Boolean) as string[];

      const filters = { signatureType: signatureTypes.INVITATION_LETTER };

      const response = (await getEnvelopesPendingBySigners({
        signerIds,
        filters,
      })) as EnvelopeWithStatus[];

      if (response.length === 0) {
        setBeneficiariesPendingEnvelopes(
          paginatedBeneficiaries as BeneficiaryWithEnvelopeId[]
        );
        setIsLoading(false);
        return;
      }

      const beneficiariesWithEnvelopeId =
        parseResponseToBeneficiariesWithEnvelopeId(
          response,
          paginatedBeneficiaries
        ) as BeneficiaryWithEnvelopeId[];

      setBeneficiariesPendingEnvelopes(beneficiariesWithEnvelopeId);
      setIsLoading(false);
    } catch (error) {
      setIsError(true);
      console.error('Error fetching beneficiaries pending envelopes:', error); // eslint-disable-line no-console
    } finally {
      setIsLoading(false);
    }
  }, [beneficiaries, currentPage, itemsPerPage, filterFn]);

  useEffect(() => {
    // Only fetch if we have beneficiaries to process
    if (beneficiaries.length > 0) {
      fetchBeneficiariesPendingEnvelopes();
    }
  }, [fetchBeneficiariesPendingEnvelopes, beneficiaries.length]);

  return {
    data: beneficiariesPendingEnvelopes,
    isLoading,
    isError,
    updateBeneficiaryStatus,
  };
};

export default useGetBeneficiariesPendingEnvelopes;
